<!--
    BD Rate-Agreement Dialog — Wave 4.2
    p-dialog mit Eingaben fuer Raten-Anzahl, Start-Datum und Intervall.
    Berechnet automatisch Raten-Betrag und Faelligkeitsdaten als Vorschau.
    @ticket TM-1000001782
-->
@if (initialized) {
    <p-dialog
            [(visible)]="visible"
            [modal]="true"
            [closable]="true"
            [closeOnEscape]="true"
            [dismissableMask]="false"
            [style]="{width: '40rem'}"
            header="Ratenvereinbarung anlegen"
            (visibleChange)="onVisibleChange($event)">

        <div class="flex flex-column gap-3 p-2">

            <!-- A4: Rahmung — Ratenvereinbarung als Alternative zur Inkasso-Übergabe -->
            <div class="surface-50 border-round p-3 text-sm text-color-secondary flex align-items-start gap-2">
                <i class="fa-light fa-handshake mt-1"></i>
                <span>
                    Die Ratenvereinbarung ist eine kundenfreundliche Alternative zur
                    Inkasso-Übergabe: Der Rückstand wird in mehreren Raten beglichen,
                    die Mahnung wird pausiert statt eskaliert.
                </span>
            </div>

            <!-- Mahnungs-Info / Kontext-Kopf (A2/A4) -->
            <div class="surface-card border-round p-3 flex flex-column gap-2">
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-user text-primary"></i>
                    <span class="font-semibold">Kunde:</span>
                    <span>{{ customerName }}</span>
                    @if (contextLoading) {
                        <i class="fa-light fa-spinner fa-spin text-color-secondary"></i>
                    }
                </div>
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-file-lines text-primary"></i>
                    <span class="font-semibold">Mahn-Typ:</span>
                    <span>{{ dunningTypeName }}</span>
                </div>
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-layer-group text-primary"></i>
                    <span class="font-semibold">Aktive Stufe:</span>
                    <span>{{ activeLevelLabel }}</span>
                </div>
                <!-- A4 / M1: Vertrags-/Lebensversicherungs-Bezug (führendes Merkmal Abrechnungskonto) -->
                @if (contractReferenceLabel) {
                    <div class="flex align-items-center gap-2">
                        <i class="fa-regular fa-file-contract text-primary"></i>
                        <span class="font-semibold">Vertrag / Lebensversicherung:</span>
                        <span>{{ contractReferenceLabel }}</span>
                    </div>
                }
                <!-- A4 / M1: Vermittler-/Zahlweg-Hinweis ('Pfad' ausgeschrieben) -->
                @if (brokerHintLabel) {
                    <div class="flex align-items-center gap-2">
                        <i class="fa-regular fa-route text-primary"></i>
                        <span class="font-semibold">Vermittler / Zahlweg:</span>
                        <span>{{ brokerHintLabel }}</span>
                    </div>
                }
                <!-- A2: Mahn-relevanter Saldo (nur Information) -->
                @if (mahnRelevanterSaldoLabel) {
                    <div class="flex align-items-center gap-2">
                        <i class="fa-regular fa-scale-balanced text-primary"></i>
                        <span class="font-semibold">Mahn-relevanter Saldo:</span>
                        <span>{{ mahnRelevanterSaldoLabel }}</span>
                    </div>
                }
                <!-- G7 / M6: schlankes Label, die Formel-Erklärung wandert in den Tooltip -->
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-coins text-primary"></i>
                    <span class="font-semibold flex align-items-center gap-1">
                        Raten-Gesamtbetrag
                        <i class="fa-regular fa-circle-question cursor-help text-color-secondary"
                           pTooltip="Entspricht dem aktuellen mahn-relevanten Rückstand zum Stichtag."
                           tooltipPosition="top"></i>:
                    </span>
                    <span class="font-semibold">{{ totalAmountLabel }}</span>
                </div>
            </div>

            <!-- G7: Start-Saldo weicht vom heutigen Rückstand ab -->
            @if (startVsHeuteDivergiert) {
                <p-message
                        severity="warn"
                        text="Start-Saldo weicht vom heutigen mahn-relevanten Rückstand ab — der Plan basiert auf dem Heute-Stand zum Stichtag."
                        styleClass="w-full">
                </p-message>
            }

            <!-- Eingaben: Raten-Anzahl + Intervall -->
            <div class="grid">
                <div class="col-12 md:col-6 flex flex-column gap-1">
                    <label class="block text-sm font-medium">
                        Raten-Anzahl <span class="text-red-500">*</span>
                    </label>
                    <input type="number"
                           pInputText
                           [min]="minRateCount"
                           [max]="maxRateCount"
                           [(ngModel)]="rateCount"
                           (ngModelChange)="onInputChange()"
                           class="w-full">
                    <small class="text-500">Erlaubt: {{ minRateCount }} bis {{ maxRateCount }} Raten</small>
                </div>

                <div class="col-12 md:col-6 flex flex-column gap-1">
                    <label class="block text-sm font-medium">
                        Intervall <span class="text-red-500">*</span>
                    </label>
                    <p-select
                            [options]="intervalOptions"
                            optionLabel="label"
                            optionValue="value"
                            [(ngModel)]="selectedInterval"
                            (onChange)="onInputChange()"
                            appendTo="body"
                            styleClass="w-full">
                    </p-select>
                </div>
            </div>

            <!-- Start-Datum -->
            <div class="flex flex-column gap-1">
                <label class="block text-sm font-medium">
                    Start-Datum <span class="text-red-500">*</span>
                </label>
                <p-datePicker
                        [(ngModel)]="startDate"
                        (ngModelChange)="onInputChange()"
                        dateFormat="dd.mm.yy"
                        [showIcon]="true"
                        appendTo="body"
                        styleClass="w-full">
                </p-datePicker>
            </div>

            <!-- Auto-berechneter Raten-Betrag -->
            <div class="surface-50 border-round p-3 flex flex-column gap-1">
                <div class="flex align-items-center justify-content-between">
                    <span class="text-sm font-medium">Berechneter Ratenbetrag:</span>
                    <span class="font-semibold text-lg">{{ formatRateAmount(rateAmount) }}</span>
                </div>
                <!-- A3: Letzte Rate trägt den Cent-Rest und kann abweichen -->
                @if (lastRateDiffers) {
                    <div class="flex align-items-center justify-content-between text-sm text-color-secondary">
                        <span>letzte Rate (mit Cent-Rest):</span>
                        <span class="font-medium">{{ formatRateAmount(lastRateAmount) }}</span>
                    </div>
                }
            </div>

            <!-- G1/G2: Künftige Monatsbelastung — drei klar beschriftete, fette Zeilen -->
            <div class="surface-card border-round p-3 flex flex-column gap-2">
                <!-- Zeile 1: Monatliche Rate -->
                <div class="flex align-items-center justify-content-between">
                    <span class="font-bold">Monatliche Rate:</span>
                    <span class="font-bold text-lg">{{ formatRateAmount(rateAmount) }}</span>
                </div>

                <!-- Zeile 2: Laufender Beitrag (monatlich) — KEIN Rückstand -->
                <div class="flex flex-column gap-1">
                    <div class="flex align-items-center justify-content-between">
                        <span class="font-bold">Laufender Beitrag (monatlich):</span>
                        @if (laufenderBeitragLabel) {
                            <span class="font-bold text-lg">{{ laufenderBeitragLabel }}</span>
                        } @else {
                            <span class="font-bold text-color-secondary">nicht ermittelbar</span>
                        }
                    </div>
                    @if (laufenderBeitragLabel) {
                        <small class="text-color-secondary">kein Rückstand — laufende Versicherungsbeiträge</small>
                    }
                </div>

                <!-- U03 / Zeile 3: Gesamtbelastung pro Monat = Rate + laufender Beitrag = Z € -->
                @if (gesamtbelastungLabel) {
                    <div class="flex flex-column gap-1 border-top-1 surface-border pt-2">
                        <div class="flex align-items-center justify-content-between">
                            <span class="font-bold text-primary text-lg">Gesamtbelastung pro Monat:</span>
                            <span class="font-bold text-xl text-primary">{{ gesamtbelastungLabel }}</span>
                        </div>
                        <!-- Ausgeschriebene Rechnung, damit Jürgen sie 1:1 vorlesen kann -->
                        <small class="text-color-secondary">
                            Rate {{ formatRateAmount(rateAmount) }} + laufender Beitrag {{ laufenderBeitragLabel }}
                            = {{ gesamtbelastungLabel }} pro Monat
                        </small>
                    </div>
                } @else {
                    <!-- U03: Klarer Erklär-Zustand statt stillem Weglassen, wenn der
                         laufende Beitrag nicht ermittelbar ist. -->
                    <div class="flex align-items-start gap-2 border-top-1 surface-border pt-2 text-color-secondary">
                        <i class="fa-regular fa-circle-info mt-1"></i>
                        <span class="text-sm">
                            Gesamtbelastung kann nicht berechnet werden, da kein laufender Beitrag
                            ermittelbar ist. Die monatliche Belastung beträgt mindestens die Rate
                            von {{ formatRateAmount(rateAmount) }}.
                        </span>
                    </div>
                }
            </div>

            <!-- G12: Tragfähigkeits-Warnung (Rate + laufender Beitrag) — KEIN harter Block -->
            @if (tragfaehigkeitWarn && gesamtbelastungLabel) {
                <p-message
                        severity="warn"
                        text="Hohe Monatsbelastung: Rate + laufender Beitrag = {{ gesamtbelastungLabel }}/Monat. Bitte Tragfähigkeit mit dem Kunden prüfen."
                        styleClass="w-full">
                </p-message>
            }

            <!-- Vorschau-Tabelle der Raten -->
            @if (installments.length > 0) {
                <div class="surface-card border-round overflow-hidden">
                    <p-table [value]="installments"
                             styleClass="p-datatable-sm p-datatable-striped">
                        <ng-template pTemplate="header">
                            <tr>
                                <th class="w-4rem">Rate Nr.</th>
                                <th>Betrag</th>
                                <th>Fälligkeit</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-item let-last="last">
                            <!-- A3: abweichende letzte Rate dezent hervorheben -->
                            <tr [ngClass]="{'surface-50': last && lastRateDiffers}">
                                <td class="font-semibold">{{ item.index }}</td>
                                <td>
                                    {{ formatRateAmount(item.amount) }}
                                    @if (last && lastRateDiffers) {
                                        <span class="text-xs text-color-secondary ml-2">(letzte Rate)</span>
                                    }
                                </td>
                                <td>{{ item.dueDate | date:'dd.MM.yyyy' }}</td>
                            </tr>
                        </ng-template>
                        <!-- C18: Summenzeile — Summe der Raten = Gesamtbetrag (Cent-Rest in letzter Rate) -->
                        <ng-template pTemplate="footer">
                            <tr>
                                <td class="font-semibold">Σ</td>
                                <td class="font-semibold">{{ installmentsSumLabel }}</td>
                                <td class="text-sm text-color-secondary">Summe der Raten = Gesamtbetrag</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>

                <!-- G11 / M6: Vorlese-Box als Hauptaussage optisch hervorgehoben
                     (Primär-Akzent, größere Schrift, telefon-tauglich) -->
                <div class="border-round border-left-3 border-primary bg-primary-50 p-3 flex align-items-start gap-3"
                     role="note"
                     aria-live="polite">
                    <i class="fa-regular fa-phone text-primary text-lg mt-1"></i>
                    <div class="flex flex-column gap-1">
                        <span class="text-xs font-bold uppercase text-primary">Zum Vorlesen für den Kunden</span>
                        <span class="font-semibold text-lg line-height-3">
                            {{ rateCount }} Raten à {{ formatRateAmount(rateAmount) }}@if (lastRateDiffers) { (letzte Rate {{ formatRateAmount(lastRateAmount) }})}, Gesamtsumme {{ installmentsSumLabel }}, letzte Fälligkeit {{ lastDueDate | date:'dd.MM.yyyy' }}.
                        </span>
                    </div>
                </div>

                <!-- C18: Plausibilitätswarnung (Rate × Laufzeit ≠ Saldo) -->
                @if (!plausibilityOk) {
                    <p-message
                            severity="warn"
                            text="Summe der Raten weicht vom Gesamtbetrag ab. Bitte Raten-Anzahl prüfen."
                            styleClass="w-full">
                    </p-message>
                }

                <!-- C18 / M6: Plan-Wirkungs-Hinweis — der kryptische Halbsatz
                     'Vorausschau = Rückstand' ist in zwei klar beschriftete Zeilen
                     aufgeteilt (Heute-Sicht vs. Vorausschau-Sicht). -->
                <div class="surface-50 border-round p-3 text-sm text-color-secondary flex flex-column gap-2">
                    <div class="flex align-items-start gap-2">
                        <i class="fa-light fa-circle-info mt-1"></i>
                        <span>Mit dem Anlegen wird die Mahnung pausiert.</span>
                    </div>
                    <div class="flex justify-content-between gap-3 pl-4">
                        <span>Heute (bei aktivem Plan):</span>
                        <span class="font-medium">0,00&nbsp;€</span>
                    </div>
                    <div class="flex justify-content-between gap-3 pl-4">
                        <span>Vorausschau (gesamter Rückstand zum Stichtag):</span>
                        <span class="font-medium">{{ totalAmountLabel }}</span>
                    </div>
                </div>
            }

            <!-- A1: Pflicht-Begründung -->
            <div class="flex flex-column gap-1">
                <label class="block text-sm font-medium">
                    Begründung <span class="text-red-500">*</span>
                </label>
                <p class="text-sm text-color-secondary m-0">
                    Bitte begründen Sie die Anlage der Ratenvereinbarung.
                </p>
                <textarea
                        pInputTextarea
                        [(ngModel)]="reason"
                        (ngModelChange)="onReasonChange()"
                        rows="3"
                        [autoResize]="true"
                        [ngClass]="{'ng-invalid ng-dirty': reasonInvalid}"
                        class="w-full"
                        placeholder="Begründung eingeben...">
                </textarea>
                @if (reasonInvalid) {
                    <small class="text-red-500">Bitte geben Sie eine Begründung ein.</small>
                }
            </div>

            <!-- Error -->
            @if (errorMessage) {
                <p-message
                        severity="error"
                        [text]="errorMessage"
                        styleClass="w-full">
                </p-message>
            }
        </div>

        <!-- Footer -->
        <ng-template #footer>
            <div class="flex justify-content-end gap-2 w-full">
                <p-button
                        label="Abbrechen"
                        severity="secondary"
                        [disabled]="submitting"
                        (onClick)="onCancel()">
                </p-button>
                <p-button
                        label="Ratenvereinbarung anlegen"
                        icon="fa-regular fa-check"
                        severity="success"
                        [loading]="submitting"
                        [disabled]="reasonMissing"
                        (onClick)="confirm()">
                </p-button>
            </div>
        </ng-template>
    </p-dialog>
}
